import PointsWaveAnimation from "@/components/lazo-ai-chat-components/PointsWaveAnimation";
import { Colors } from "@/enums/Colors";
import { InputAdornment, styled, SxProps, Tooltip, tooltipClasses, TooltipProps } from "@mui/material";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { FieldError } from "react-hook-form";
import { useTranslation } from "react-i18next";
import informationSVG from "../../../../assets/images/Information.svg";
type Props = {
  onChange: (...event: any[]) => void;
  value: any;
  error: FieldError;
  disabled?: boolean;
  textfieldSx?: SxProps;
  isIndividual?: boolean;
  checkingTaken: boolean;
  available: boolean;
  autoComplete?: string;
};
interface StyledToolTipProps extends TooltipProps {
  noteTooltip?: boolean;
}

function getStatusMessage(error: FieldError, checkingTaken: boolean, available: boolean = false, disabled: boolean = false, t: any) {
  if (checkingTaken || disabled) {
    return null; // Do not show any message while checking
  }

  if (error) {
    switch (error.type) {
      case "pattern": {
        return (
          <Typography fontSize="0.75rem" color="red">
            {t("workspace.validation.pattern")}
          </Typography>
        );
      }
      case "required": {
        return (
          <Typography fontSize="0.75rem" color="red">
            {t("workspace.validation.required")}
          </Typography>
        );
      }
      default: {
        return (
          <Typography fontSize="0.75rem" color="red">
            {t("workspace.validation.taken")}
          </Typography>
        );
      }
    }
  }
  if (available)
    return (
      <Typography fontSize="0.75rem" color="green">
        {t("workspace.available")}
      </Typography>
    );
}

export default function WorkspaceTextfield(props: Props) {
  const { t } = useTranslation("auth");

  return (
    <Box display="flex" flexDirection="column" gap="0.5rem" height="75px">
      <Box display="flex" gap="0.75rem" alignItems="center">
        <TextField
          onChange={props.onChange}
          autoComplete={props.autoComplete ?? "off"}
          value={props.value}
          error={!!props.error}
          disabled={props.disabled}
          placeholder={t("workspace.placeholder")}
          sx={{ ...props.textfieldSx }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <StyledToolTip
                  arrow
                  placement="top"
                  className="tooltip"
                  noteTooltip
                  disableHoverListener={props.isIndividual == null}
                  title={props.isIndividual ? "" : t("workspace.tooltip")}
                >
                  <img src={informationSVG.src} alt={t("workspace.altText")} />
                </StyledToolTip>
              </InputAdornment>
            ),
          }}
        />
        <Typography>lazo.one</Typography>
      </Box>
      {props.checkingTaken ? (
        <PointsWaveAnimation
          points={3}
          pointsColor={Colors.LAZO_PURPLE}
          containerProps={{
            sx: {
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            },
          }}
        />
      ) : (
        getStatusMessage(props.error, props.checkingTaken, props.available, props.disabled, t)
      )}
    </Box>
  );
}

const StyledToolTip = styled(({ className, noteTooltip, ...props }: StyledToolTipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme, noteTooltip }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.white,
    filter: "drop-shadow(0px 2px 4px rgba(0,0,0,0.1))",
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: Colors.LIGHT_GRAY_8A8E93,
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
    borderRadius: "0.75rem",
    padding: "0.75rem",
    fontSize: "0.875rem",
    maxWidth: 150,
    "& a": {
      color: theme.palette.primary.main,
    },
  },
}));
